/**
 * useMediaPicker
 *
 * Thin shared hook that manages open/close state, the current selectedAssets list,
 * and the onSelect/onConfirm callback. Components no longer wire their own
 * `mediaPickerOpen` boolean and `selectedAssets` array — they use this instead.
 *
 * Usage:
 *   const { pickerProps, selectedAssets, openPicker, clearSelection } = useMediaPicker({
 *     multiSelect: false,
 *     onConfirm: (assets) => { ... },
 *   })
 *   // Spread pickerProps directly onto <MediaPickerModal {...pickerProps} />
 */

import { useCallback, useState } from 'react'
import type { MediaAsset } from '@/services/folders/mediaTypes'

interface UseMediaPickerOptions {
    /** Allow selecting multiple assets. Defaults to false. */
    multiSelect?: boolean
    /**
     * Called when the user confirms their selection.
     * In single-select mode this fires immediately on click with a one-element array.
     * In multi-select mode callers must add a confirm button that calls this manually.
     */
    onConfirm: (assets: MediaAsset[]) => void
}

interface MediaPickerProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    selectedAssets: MediaAsset[]
    onSelect: (asset: MediaAsset) => void
    multiSelect: boolean
}

interface UseMediaPickerReturn {
    /** Spread these directly onto <MediaPickerModal /> */
    pickerProps: MediaPickerProps
    /** Currently selected assets (always an array, never null). */
    selectedAssets: MediaAsset[]
    /** Open the picker. */
    openPicker: () => void
    /** Clear the selection without closing. */
    clearSelection: () => void
}

export function useMediaPicker({
    multiSelect = false,
    onConfirm,
}: UseMediaPickerOptions): UseMediaPickerReturn {
    const [open, setOpen] = useState(false)
    const [selectedAssets, setSelectedAssets] = useState<MediaAsset[]>([])

    const openPicker = useCallback(() => setOpen(true), [])

    const clearSelection = useCallback(() => setSelectedAssets([]), [])

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            setOpen(isOpen)
            if (!isOpen) {
                setSelectedAssets([])
            }
        },
        [],
    )

    const handleSelect = useCallback(
        (asset: MediaAsset) => {
            if (multiSelect) {
                setSelectedAssets(prev => {
                    const already = prev.some(a => a._id === asset._id)
                    return already
                        ? prev.filter(a => a._id !== asset._id)
                        : [...prev, asset]
                })
            } else {
                // Single-select: confirm immediately, then close and reset.
                onConfirm([asset])
                setOpen(false)
                setSelectedAssets([])
            }
        },
        [multiSelect, onConfirm],
    )

    const pickerProps: MediaPickerProps = {
        open,
        onOpenChange: handleOpenChange,
        selectedAssets,
        onSelect: handleSelect,
        multiSelect,
    }

    return { pickerProps, selectedAssets, openPicker, clearSelection }
}
